import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import Loader from 'react-loader-spinner';
import { FiPlus, FiSearch, FiUsers } from 'react-icons/fi';
import { API_URLS } from '../constants/config';
import { useI18n } from '../utilities/i18n';
import { authorizedPost, getApiErrorMessage } from '../utilities/stadiumsApi';
import { useChampionships } from '../utilities/championships';
import { IfCanWrite, getChampsIds, hasUnrestrictedChamps } from '../utilities/adminAccess';
import { useAuth } from '../utilities/auth';
import {
  USERS_PAGE_SIZE,
  USER_ROLES,
  assignmentLabel,
  extractUsersPage,
  roleAssignmentKey,
  userCardPath,
  userDisplayName,
  userNewPath,
} from '../utilities/usersApi';
import ReorderableTable from '../components/ReorderableTable';
import StadiumCrumbs from '../components/StadiumCrumbs';
import '../styles/stadiumsManagement.css';
import '../styles/usersManagement.css';

export default function UsersManagement() {
  const dispatch = useDispatch();
  const { t } = useI18n();
  const { championships } = useChampionships();
  const { userData } = useAuth();
  const roleOptions = USER_ROLES;
  const champOptions = useMemo(() => {
    if (hasUnrestrictedChamps(userData)) return championships;
    const allowed = new Set(getChampsIds(userData));
    if (!allowed.size) return [];
    return championships.filter((item) => allowed.has(Number(item.id)));
  }, [championships, userData]);

  const [users, setUsers] = useState([]);
  const [total, setTotal] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [champFilter, setChampFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });
  const searchTimer = useRef(null);
  const requestIdRef = useRef(0);
  const usersRef = useRef([]);
  usersRef.current = users;

  useEffect(() => {
    if (searchTimer.current) clearTimeout(searchTimer.current);
    const delay = searchQuery ? 350 : 0;
    searchTimer.current = setTimeout(() => {
      loadUsers(searchQuery, roleFilter, champFilter, 0, false);
    }, delay);
    return () => {
      if (searchTimer.current) clearTimeout(searchTimer.current);
    };
  }, [searchQuery, roleFilter, champFilter]);

  const loadUsers = async (search, role, champ, offset, append) => {
    const requestId = ++requestIdRef.current;
    if (append) setLoadingMore(true);
    else {
      setLoading(true);
      setMessage({ text: '', type: '' });
    }

    try {
      const body = {
        limit: USERS_PAGE_SIZE,
        offset,
      };
      if (search && search.trim()) body.search = search.trim();
      if (role) body.roles = [Number(role)];
      if (champ) body.championat_id = Number(champ);

      const response = await authorizedPost(dispatch, API_URLS.getAdminUsers, body);
      if (requestId !== requestIdRef.current) return;

      const page = extractUsersPage(response.data);
      const nextUsers = append ? usersRef.current.concat(page.items) : page.items;
      setUsers(nextUsers);
      setTotal(page.total);
      setHasMore(
        page.total != null
          ? nextUsers.length < page.total
          : page.items.length === USERS_PAGE_SIZE
      );
    } catch (error) {
      if (requestId !== requestIdRef.current) return;
      if (!append) {
        setUsers([]);
        setTotal(null);
        setHasMore(false);
      }
      setMessage({ text: getApiErrorMessage(error, t('usrLoadError')), type: 'error' });
    } finally {
      if (requestId === requestIdRef.current) {
        setLoading(false);
        setLoadingMore(false);
      }
    }
  };

  const columns = [
    {
      key: 'login',
      label: t('usrLogin'),
      className: 'st-cell-name',
      render: (item) => {
        const name = userDisplayName(item);
        return (
          <span className="usr-login-cell">
            <span>{item.login || '—'}</span>
            {name ? <span className="usr-login-name">{name}</span> : null}
          </span>
        );
      },
    },
    {
      key: 'id',
      label: t('usrId'),
      className: 'usr-cell-id hide-sm',
      render: (item) => item.id || '—',
    },
    {
      key: 'roles',
      label: t('usrRoles'),
      className: 'usr-cell-roles',
      render: (item) => {
        const roles = Array.isArray(item.roles) && item.roles.length
          ? item.roles
          : (item.role != null ? [{ role: item.role }] : []);
        if (!roles.length) return '—';
        return (
          <span className="usr-chips">
            {roles.map((assignment) => (
              <span
                key={roleAssignmentKey(assignment)}
                className={`usr-chip ${Number(assignment.role) === Number(item.role) ? 'is-main' : ''}`}
              >
                {assignmentLabel(t, assignment)}
              </span>
            ))}
          </span>
        );
      },
    },
  ];

  const foundCount = total != null ? total : users.length;

  return (
    <div className="layoutWrapper st-layout">
      <div className="matchCenterWrapper st-page">
        <div className="matchCenterHeader">
          <div className="st-header">
            <StadiumCrumbs items={[{ label: t('navUsers') }]} />
          </div>
        </div>

        <div className="st-toolbar">
          <div className="st-search">
            <FiSearch className="st-search-icon" />
            <input
              type="text"
              className="st-search-input"
              placeholder={t('usrSearchPh')}
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
            />
            {searchQuery ? (
              <button type="button" className="st-search-clear" onClick={() => setSearchQuery('')}>
                ×
              </button>
            ) : null}
          </div>
          <div className="usr-toolbar-row">
            <select
              className="st-select usr-filter-select"
              value={roleFilter}
              onChange={(event) => setRoleFilter(event.target.value)}
            >
              <option value="">{t('usrAllRoles')}</option>
              {roleOptions.map((role) => (
                <option key={role.id} value={role.id}>{t(role.labelKey)}</option>
              ))}
            </select>
            <select
              className="st-select usr-filter-select"
              value={champFilter}
              onChange={(event) => setChampFilter(event.target.value)}
            >
              <option value="">{t('usrAllChamps')}</option>
              {champOptions.map((champ) => (
                <option key={champ.id || champ.value} value={champ.id || champ.value}>{champ.name}</option>
              ))}
            </select>
            <div className="st-meta">
              <strong>{t('usrFound', { count: foundCount })}</strong>
            </div>
          </div>
        </div>

        {message.text ? <div className={`st-message ${message.type}`}>{message.text}</div> : null}

        <div className="st-scroll">
          <div className="st-content">
            <div className="st-block">
              <h2 className="st-block-header">{t('navUsers')}</h2>
              {loading ? (
                <div className="st-loading">
                  <Loader type="Oval" color="#3ad1a0" height={48} width={48} />
                  <p>{t('usrLoading')}</p>
                </div>
              ) : (
                <>
                  <ReorderableTable
                    tableClassName="st-table-users"
                    columns={columns}
                    items={users}
                    getRowKey={(item, index) => item.id || item.user_id || index}
                    reorderMode={false}
                    getRowHref={(item) => {
                      const id = item.id || item.user_id;
                      return id ? userCardPath(id) : null;
                    }}
                    empty={(
                      <div className="st-empty">
                        <FiUsers size={48} />
                        <h3>{t('usrEmpty')}</h3>
                        <p>{t('usrEmptyHint')}</p>
                      </div>
                    )}
                  />
                  {hasMore ? (
                    <div className="usr-load-more">
                      <button
                        type="button"
                        className="st-btn st-btn-secondary"
                        disabled={loadingMore}
                        onClick={() => loadUsers(searchQuery, roleFilter, champFilter, users.length, true)}
                      >
                        {loadingMore ? t('usrLoading') : t('dqLoadMore')}
                      </button>
                    </div>
                  ) : null}
                </>
              )}
            </div>
          </div>
        </div>

        <div className="st-actions">
          <IfCanWrite entity="users">
            <Link
              to={userNewPath()}
              className={`st-btn st-btn-primary${loading ? ' is-disabled' : ''}`}
              aria-disabled={loading || undefined}
              onClick={(event) => { if (loading) event.preventDefault(); }}
            >
              <FiPlus /> {t('usrCreate')}
            </Link>
          </IfCanWrite>
        </div>
      </div>
    </div>
  );
}
